import * as React from 'react';
import { styled } from '@glitz/react';
import { Button } from 'webapp/components/button';
import { Theme, isNordicNest } from 'webapp/theme';
import { FieldSize } from 'webapp/components/field-size';
import { black } from 'webapp/shared/sharedTheme';
import { bareListDecorator } from 'webapp/decorators/list';
import { createMaxSmallViewportDecorator } from 'webapp/viewport';
import { useMatchLargeViewport } from '@avensia/excite';
import { DesktopFacetButton } from './DesktopFacetButton';
import { selector } from '@glitz/core';
import { toFacetValue } from './BooleanFacet';

type PropType = {
  highlightedFacets?: Excite.Esales.FacetApiModel[];
  selectedFacets?: Excite.Esales.FacetApiModel[];
  openHighlightedFacet: (facet: Excite.Esales.FacetApiModel) => void;
  onFacetValueChange: (
    updatedFacet: Excite.Esales.FacetApiModel,
    changedValue: Excite.Esales.FacetValueApiModel,
  ) => void;
};

export const HighlightedFacets = (props: PropType) => {
  const isDesktop = useMatchLargeViewport();

  const handleClick = React.useCallback(
    (facet: Excite.Esales.FacetApiModel) => {
      if (facet.values[0]?.text === 'true' || facet.values[0]?.text === 'false') {
        props.onFacetValueChange(
          {
            ...facet,
            values: [toFacetValue(!facet.values[0]?.selected)],
          },
          toFacetValue(!facet.values[0]?.selected),
        );
      } else {
        props.openHighlightedFacet(facet);
      }
    },
    [props],
  );

  return (
    <Base>
      {props.highlightedFacets?.map(highlightedFacet =>
        isDesktop ? (
          <DesktopFacetButton
            key={highlightedFacet.attribute}
            highlightedFacet={highlightedFacet}
            selectedFacets={props.selectedFacets}
            openHighlightedFacet={props.openHighlightedFacet}
            onFacetValueChange={props.onFacetValueChange}
          />
        ) : (
          <HighlightedFacet key={highlightedFacet.attribute}>
            <FacetButton
              as={Theme.White}
              size={FieldSize.XXSmall}
              onClick={() => handleClick(highlightedFacet)}
              css={props.selectedFacets?.find(f => f.attribute === highlightedFacet.attribute) && activeDecorator}
            >
              {highlightedFacet.text}
            </FacetButton>
          </HighlightedFacet>
        ),
      )}
    </Base>
  );
};

const Base = styled.ul(
  bareListDecorator,
  createMaxSmallViewportDecorator({
    overflowX: 'auto',
    gap: t => t.sharedSpacing.xxs,
  }),
  {
    gridArea: 'highlight',
    display: 'flex',
    gap: t => t.sharedSpacing.xs,
    padding: { xy: 1 },
  },
  selector('::-webkit-scrollbar', {
    display: 'none',
  }),
);
const HighlightedFacet = styled.li({
  flexShrink: 0,
  ':last-child': {
    marginRight: t => t.sharedSpacing.xs,
  },
});

const FacetButton = styled(Button, {
  flexShrink: 0,
  fontSize: t => t.sharedFontSize.xs,
  fontWeight: t => (isNordicNest ? t.fontWeight?.bold : t.fontWeight?.normal),
  padding: { x: t => t.sharedSpacing.xs },
  height: 32,
});

const activeDecorator = styled({
  border: { xy: { color: black } },
});
